import type { ReactNode } from 'react';
import { cx } from '@/shared/ui/cx';
import { CheckIcon } from '@/shared/ui/icons';

export type CheckboxProps = {
  /** Visible text beside the box. Also the control's accessible name. */
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Sits after the label — an info affordance, a hint, a count. */
  adornment?: ReactNode;
  className?: string;
};

/**
 * A binary opt-in with a default: one choice, off unless the user turns it on.
 *
 * Distinct from `Segmented`, which is for a small set of mutually exclusive
 * choices where neither is privileged. A checkbox says "this is the ordinary
 * behaviour, and here is the departure from it" — two segments side by side say
 * the two options are peers, which costs twice the width to express less.
 *
 * A real `<input type="checkbox">` under a styled box, rather than a button with
 * `aria-checked`. The native control brings keyboard behaviour, form semantics,
 * the indeterminate state if it is ever needed, and — the one that actually
 * decides it — correct announcement on every screen reader without this file
 * having an opinion about any of them. The input is visually hidden, not
 * `display: none`, so it stays focusable and the focus ring is drawn from
 * `:focus-visible` on it.
 *
 * `adornment` renders OUTSIDE the label element. Anything interactive nested
 * inside a `<label>` fights the label for the click: tapping an info button
 * would also toggle the box, which is a control doing two things at once.
 */
export function Checkbox({
  label,
  checked,
  onChange,
  disabled = false,
  adornment,
  className,
}: CheckboxProps) {
  return (
    <div className={cx('checkbox', disabled && 'is-disabled', className)}>
      <label className="checkbox__label">
        <input
          type="checkbox"
          className="checkbox__input"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="checkbox__box" aria-hidden="true">
          <CheckIcon size={14} />
        </span>
        <span className="checkbox__text">{label}</span>
      </label>
      {adornment}
    </div>
  );
}
